import { HttpTypes } from "@medusajs/types"

type LineItem = HttpTypes.StoreCartLineItem

const FEE_METADATA_TYPES = new Set(["personalization_fee", "patch_fee"])

const isCustomizationFeeItem = (item: LineItem): boolean => {
  const type = (item.metadata as Record<string, unknown> | null)?.type
  return typeof type === "string" && FEE_METADATA_TYPES.has(type)
}

export type GroupedCartLineItem = {
  item: LineItem
  feesTotal: number
}

/**
 * Customisation (name/number printing, a patch) is stored as separate cart
 * line items linked back to their product via metadata.parent_line_item_id
 * (see admin-medusa's addCustomizationItemsStep) so Medusa can price them
 * independently. The reference site folds these into the product's own row
 * instead of showing them as their own product card — this groups them back
 * together so the storefront can render one card per product.
 */
export function groupCustomizationFees(
  items: LineItem[]
): GroupedCartLineItem[] {
  const feeItems = items.filter(isCustomizationFeeItem)
  const productItems = items.filter((item) => !isCustomizationFeeItem(item))

  const feesByParent = new Map<string, LineItem[]>()
  feeItems.forEach((fee) => {
    const parentId = (fee.metadata as Record<string, unknown> | null)
      ?.parent_line_item_id as string | undefined
    if (!parentId) return
    const existing = feesByParent.get(parentId) ?? []
    existing.push(fee)
    feesByParent.set(parentId, existing)
  })

  return productItems.map((item) => {
    const fees = feesByParent.get(item.id) ?? []
    const feesTotal = fees.reduce(
      (sum, fee) => sum + fee.unit_price * fee.quantity,
      0
    )
    return { item, feesTotal }
  })
}
